import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { ProcessTaskTypeOption } from '../../../api/process-client/process-client.type';
import type { ProjectsResponse, Subproject } from '../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import type { WorkPeriod } from '../../../api/work-periods-client/work-periods-client.type';
import type { Workflow } from '../../../api/workflows-client/workflows-client.type';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';
import { snakeCaseToTitleCase } from '../../../helpers/string-helpers/string-helpers';
import { TaskType, taskTypeI18nKeys } from '../../tasks-table/tasks-table.type';
import type { ProcessScope } from '../context/process.context.type';

/**
 * Returns the translated label for a task type.
 * Uses i18n dictionary for known types with custom translations, falls back to snakeCaseToTitleCase for others.
 *
 * @param {string} type - The task type value
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The translated or formatted label
 */
function getTaskTypeLabel(type: string, t: TFunction): string {
  const i18nKey = taskTypeI18nKeys[type];
  return i18nKey ? t(i18nKey) : snakeCaseToTitleCase(type);
}

/**
 * Builds a value→label lookup from the labeled options returned by the
 * process task-types endpoint. Only genuinely verbatim labels are kept:
 * entries whose label is empty or merely repeats the value (the backend's
 * fallback when no label has been synced) are dropped, so consumers that
 * miss the lookup keep today's legacy rendering for those types.
 *
 * @param {ProcessTaskTypeOption[] | null | undefined} options - Labeled options from the endpoint; may be absent while loading or on old backends
 * @returns {Record<string, string>} Map of raw_type value to verbatim external label
 */
function buildTaskTypeLabelMap(options?: ProcessTaskTypeOption[] | null): Record<string, string> {
  const labelsByValue: Record<string, string> = {};
  (options ?? []).forEach((option) => {
    if (option.label && option.label !== option.value) {
      labelsByValue[option.value] = option.label;
    }
  });
  return labelsByValue;
}

/**
 * Creates filter groups for a process section (left or right)
 *
 * @param params Configuration parameters for creating filter groups
 * @returns Array of filter groups
 */
const createProcessFilterGroups = (params: {
  teams: ProjectsResponse[];
  team: ProjectsResponse | null;
  board: Subproject | null;
  taskTypes: string[] | null;
  selectedTaskTypes: string[] | null;
  epic: Epic | null;
  epics: Epic[];
  useWorkflow: boolean;
  workflow: Workflow | null;
  workflows: Workflow[];
  allSeenTaskTypesRef: React.MutableRefObject<Set<string>>;
  taskTypeLabelsByValue?: Record<string, string>;
  onTeamSelected: (team: ProjectsResponse) => void;
  onBoardSelected: (board: Subproject | null) => void;
  onTaskTypeSelected: (taskTypes: string[] | null) => void;
  onEpicSelected: (epic: Epic | null) => void;
  onWorkflowChange: (workflowId: string | null) => void;
  workPeriods?: WorkPeriod[];
  selectedWorkPeriod?: WorkPeriod | null;
  onWorkPeriodSelected?: (workPeriod: WorkPeriod | null) => void;
  t: TFunction;
}): FilterGroup[] => {
  const {
    teams,
    team,
    board,
    taskTypes,
    selectedTaskTypes,
    epic,
    epics,
    useWorkflow,
    workflow,
    workflows,
    allSeenTaskTypesRef,
    taskTypeLabelsByValue,
    workPeriods = [],
    selectedWorkPeriod,
    onWorkPeriodSelected,
    onTeamSelected,
    onBoardSelected,
    onTaskTypeSelected,
    onEpicSelected,
    onWorkflowChange,
    t,
  } = params;

  const groups: FilterGroup[] = [];

  // Team filter group
  if (!useWorkflow) {
    const teamFilterGroup: FilterGroup = {
      name: t('common.project'),
      options: teams.map((t) => ({ value: t.id, label: t.name })),
      selectedValues: team ? [team.id] : [],
      optional: false, // Make it required
      searchable: true,
      onSelectionChange: (values) => {
        if (values.length > 0) {
          const selectedTeam = teams.find((t) => t.id === values[0]);
          if (selectedTeam) {
            onTeamSelected({ ...selectedTeam });
          } else if (teams.length > 0) {
            // If selection is invalid but teams exist, select the first team
            onTeamSelected({ ...teams[0] });
          }
        } else if (teams.length > 0) {
          // Don't allow deselection - reselect the current team or first team
          const teamToSelect = team || teams[0];
          onTeamSelected({ ...teamToSelect });
        }
      },
    };
    groups.push(teamFilterGroup);
  } else {
    const workflowFilterGroup: FilterGroup = {
      name: t('data_management.workflows.workflow'),
      options: workflows?.map((w) => ({ value: w.id, label: w.name })) || [],
      selectedValues: workflow ? [workflow.id] : [],
      optional: false, // Make it required (already was, but being explicit)
      onSelectionChange: (values) => {
        if (values.length > 0) {
          onWorkflowChange(values[0] as string);
        } else if (workflows && workflows.length > 0) {
          // Don't allow deselection - reselect the current workflow or first workflow
          const workflowToSelect = workflow?.id || workflows[0].id;
          onWorkflowChange(workflowToSelect);
        }
      },
    };
    groups.push(workflowFilterGroup);
  }

  // Create board option objects for the select
  const boardOptionsForSelect =
    useWorkflow && workflow
      ? getWorkflowBoardOptions(teams, workflow).map((b) => ({ value: b.id, label: b.name }))
      : team
        ? team.subprojects?.map((b) => ({ value: b.id, label: b.name })) || []
        : [];

  // Board filter group - use different boards based on whether workflow is active
  const boardFilterGroup: FilterGroup = {
    name: t('common.board'),
    options: boardOptionsForSelect,
    optional: true,
    searchable: true,
    selectedValues: board ? [board.id] : [],
    onSelectionChange: (values) => {
      if (values.length > 0) {
        // Find the selected board from the appropriate source
        const boardsToSearchIn =
          useWorkflow && workflow ? getWorkflowBoardOptions(teams, workflow) : team?.subprojects || [];

        const selectedBoard = boardsToSearchIn.find((b) => b.id === values[0]);

        if (selectedBoard) {
          onBoardSelected(selectedBoard);
        } else {
          onBoardSelected(null);
        }
      } else {
        onBoardSelected(null);
      }
    },
  };
  groups.push(boardFilterGroup);

  // Work Period filter group - only show if board is selected
  if (board && onWorkPeriodSelected) {
    const workPeriodFilterGroup: FilterGroup = {
      name: t('common.work_period') || 'Work Period',
      options: [...workPeriods]
        .sort((a, b) => dayjs(b.start_date).diff(dayjs(a.start_date)))
        .map((wp) => ({
          value: wp.id,
          label: wp.name,
        })),
      optional: true,
      searchable: true,
      tooltip: `${t('common.work_period')} selection overrides the date range filters. The Process Map only considers status transitions occurring within the selected ${t('common.work_period').toLowerCase()}.`,
      selectedValues: selectedWorkPeriod ? [selectedWorkPeriod.id] : [],
      onSelectionChange: (values) => {
        if (values.length > 0) {
          const selected = workPeriods.find((wp) => wp.id === values[0]);
          if (selected) {
            onWorkPeriodSelected(selected);
          } else {
            onWorkPeriodSelected(null);
          }
        } else {
          onWorkPeriodSelected(null);
        }
      },
    };
    groups.push(workPeriodFilterGroup);
  }

  // Update seen task types with new ones from taskTypes array.
  // Deduplicate case-insensitively: skip any incoming type whose lowercase form
  // already matches an existing entry so "Bug" and "bug" don't both appear.
  if (taskTypes?.length) {
    taskTypes.forEach((type) => {
      const lowerType = type.toLowerCase();
      const alreadyExists = Array.from(allSeenTaskTypesRef.current).some(
        (existing) => existing.toLowerCase() === lowerType,
      );
      if (!alreadyExists) {
        allSeenTaskTypesRef.current.add(type);
      }
    });
  }

  // Prefer the verbatim external label synced by the backend; types without
  // one (graph-hydrated types, base enum values, pre-resync data) keep the
  // legacy i18n/title-case rendering.
  const resolveDropdownLabel = (type: string): string => taskTypeLabelsByValue?.[type] ?? getTaskTypeLabel(type, t);

  // Get task types from the cached values and fall back to the enum
  const customTaskTypes = selectedTaskTypes
    ? selectedTaskTypes.filter((type) => !Object.values(TaskType).includes(type as TaskType))
    : [];
  const taskTypeOptions = Array.from(allSeenTaskTypesRef.current).map((type) => ({
    value: type,
    label: resolveDropdownLabel(type),
  }));

  // If there are custom task types selected that aren't in the options, add them
  customTaskTypes.forEach((customType) => {
    if (!taskTypeOptions.some((option) => option.value === customType)) {
      taskTypeOptions.push({
        value: customType,
        label: resolveDropdownLabel(customType),
      });
      allSeenTaskTypesRef.current.add(customType);
    }
  });

  // Distinct values can resolve to the same display label (case-variant
  // raw_type rows like "Task"/"task", or a graph-hydrated raw string
  // colliding with a verbatim-labeled endpoint value). Do NOT dedupe them:
  // unlike the Process Analysis filter, the process graph's task_types
  // filter is case-SENSITIVE (`Q(raw_type__in=task_types) |
  // Q(type__in=task_types)`), so case-variant values select DIFFERENT task
  // rows and collapsing them would make one variant's tasks unfilterable.
  // Instead, disambiguate every colliding entry by appending its raw value;
  // entries with unique labels are untouched. This runs AFTER the seen-types
  // and custom-selection merges above so graph-hydrated types participate.
  const labelCounts = new Map<string, number>();
  taskTypeOptions.forEach((option) => {
    labelCounts.set(option.label, (labelCounts.get(option.label) ?? 0) + 1);
  });
  const disambiguatedTaskTypeOptions = taskTypeOptions.map((option) =>
    (labelCounts.get(option.label) ?? 0) > 1 ? { ...option, label: `${option.label} (${option.value})` } : option,
  );

  // Task Type filter group
  const taskTypeFilterGroup: FilterGroup = {
    name: `${t('common.work_unit')} Type`,
    options: disambiguatedTaskTypeOptions,
    optional: true,
    selectedValues: selectedTaskTypes ?? [],
    multiSelect: true,
    onSelectionChange: (values) => {
      onTaskTypeSelected(values.length > 0 ? values : null);
    },
  };
  groups.push(taskTypeFilterGroup);

  if (!useWorkflow) {
    const epicFilterGroup: FilterGroup = {
      name: t('common.parent_task'),
      options: epics.map((e) => ({ value: e.id, label: e.name })),
      optional: true,
      selectedValues: epic ? [epic.id] : [],
      onSelectionChange: (values) => {
        if (values.length > 0) {
          const selectedEpic = epics.find((e) => e.id === values[0]);
          if (selectedEpic) {
            onEpicSelected({ ...selectedEpic });
          } else {
            onEpicSelected(null);
          }
        } else {
          onEpicSelected(null);
        }
      },
    };
    groups.push(epicFilterGroup);
  }

  return groups;
};

/**
 * Helper function to calculate workflow board options
 *
 * @param teams All available teams
 * @param workflow Current workflow
 * @returns Board options for the current workflow
 */
const getWorkflowBoardOptions = (teams: ProjectsResponse[], workflow: Workflow): Subproject[] => {
  if (!workflow) return [];

  const options: Subproject[] = [];

  teams.forEach((team) => {
    if (team.subprojects) {
      team.subprojects.forEach((subproject) => {
        if (workflow.subprojects.includes(subproject.id)) {
          options.push(subproject);
        }
      });
    }
  });

  return options;
};

/**
 * Creates a scope update function that works with handleWorkflowSelected
 *
 * @param setScope The scope setter function
 * @param workflows Available workflows
 * @param teams Available teams
 * @returns A function that handles workflow toggle selection
 */
const createWorkflowSelectHandler = (
  setScope: (scope: Partial<ProcessScope>) => void,
  workflows: Workflow[],
  teams: ProjectsResponse[],
) => {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;

    if (isChecked) {
      // Toggle ON - First clear team, board, and epic, then set workflow
      setScope({
        team: null,
        board: null,
        epic: null,
        useWorkflow: true,
      });

      // Then select the first workflow in a separate update
      setTimeout(() => {
        const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
        if (firstWorkflow) {
          setScope({ workflow: firstWorkflow });
        }
      }, 0);
    } else {
      // Toggle OFF - First clear workflow and set useWorkflow false
      setScope({
        workflow: null,
        board: null,
        epic: null,
        useWorkflow: false,
        useGroups: true,
      });

      // Then select the first team in a separate update
      setTimeout(() => {
        const firstTeam = teams && teams.length > 0 ? teams[0] : null;
        if (firstTeam) {
          setScope({ team: firstTeam });
        }
      }, 0);
    }
  };
};

/**
 * Creates a handler for resetting filters
 *
 * @param setScope The scope setter function
 * @param useWorkflow Whether workflow view is active
 * @param workflows Available workflows
 * @param teams Available teams
 * @returns A function that handles resetting filters
 */
const createResetFiltersHandler = (
  setScope: (scope: Partial<ProcessScope>) => void,
  useWorkflow: boolean,
  workflows: Workflow[],
  teams: ProjectsResponse[],
) => {
  return () => {
    // When resetting filters, still maintain team or workflow selection
    if (useWorkflow) {
      const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
      setScope({
        taskTypes: null,
        epic: null,
        board: null,
        workflow: firstWorkflow,
        useWorkflow: true,
        workPeriod: null,
      });
    } else {
      const firstTeam = teams && teams.length > 0 ? teams[0] : null;
      setScope({
        taskTypes: null,
        epic: null,
        board: null,
        workflow: null,
        useWorkflow: false,
        useGroups: true,
        team: firstTeam,
        workPeriod: null,
      });
    }
  };
};

export { buildTaskTypeLabelMap, createProcessFilterGroups, createResetFiltersHandler, createWorkflowSelectHandler };
